import { CircularProgress, IconButton, Typography } from '@mui/material';
import { Divider } from 'further-ui/components/Layout';
import { Control, Controller, useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import NumberInput from 'further-ui/components/NumberInput';
import { CalendarDay, numberToDisplayString } from 'further-ui/utils';
import { PartialRealisationAllocationLogic } from 'further-types/lp/realisation';
import UnitToInvestmentAllocationTable from './UnitToInvestmentAllocationTable';
import useHoldingClassFields from './hooks/useHoldingClassFields';
import useUnitAllocation from './hooks/useUnitAllocation';
import { type InvestmentHistory } from '../types';
import { PARTIAL_REALISATION_ALLOCATION_OPTIONS } from './constants';
import { Column, Row } from 'further-ui/components/Layout';
import TypographyButton from 'further-ui/components/TypographyButton';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import { Delete } from '@mui/icons-material';

type HoldingClassRealisationProps = {
  index: number;
  holdingClassOptions: Array<{
    value: string;
    label: string;
  }>;
  control: Control<rules.CreateRealisationFormValues>;
  holdingClassTotals: Array<{
    holdingClassId: string;
    currentNumberOfUnits: number;
  }>;
  investmentHistory: InvestmentHistory;
  assetId?: string;
  vehicleId: string;
  ledgerDate?: CalendarDay;
  onRemoveHoldingClass: () => void;
  clearConsiderationForm: () => void;
  totalClassesIncludedInRealisation: number;
};

const HoldingClassRealisation: React.FC<HoldingClassRealisationProps> = ({
  index,
  holdingClassOptions,
  control,
  holdingClassTotals,
  investmentHistory,
  assetId,
  vehicleId,
  ledgerDate,
  totalClassesIncludedInRealisation,
  onRemoveHoldingClass,
  clearConsiderationForm,
}: HoldingClassRealisationProps) => {
  const { watch, setError, clearErrors, setValue, getValues } =
    useFormContext<rules.CreateRealisationFormValues>();

  const {
    unitsSoldFieldName,
    selectedHoldingClassId,
    selectedPartialRealisationAllocationLogic,
    unitsSold,
    selectedPartialRealisationAllocationLogicFieldName,
  } = useHoldingClassFields({ index, watch });

  const holdingClassTotal = holdingClassTotals.find(
    (total) => total.holdingClassId === selectedHoldingClassId,
  ) ?? {
    holdingClassId: '',
    currentNumberOfUnits: 0,
  };

  const investmentsWithUnits = investmentHistory.filter(
    (investment) => investment.currentNumberOfUnits > 0,
  );

  const { isLoading, onManualAllocationChange, unitsAllocationPerInvestment } =
    useUnitAllocation({
      index,
      unitsSold: unitsSold ?? 0,
      selectedPartialRealisationAllocationLogic,
      assetId,
      vehicleId,
      ledgerDate,
      setValue,
      getValues,
      selectedHoldingClassId,
    });

  const handleUnitsSoldValueChange = (
    val: number | null,
    onChange: (value: number | undefined) => void,
  ) => {
    const numValue = val ?? 0;
    onChange(numValue);

    if (numValue && numValue > holdingClassTotal.currentNumberOfUnits) {
      setError(unitsSoldFieldName, {
        type: 'validate',
        message: `The maximum number of units available for sale are ${holdingClassTotal.currentNumberOfUnits}. Please change the number to be sold and try again.`,
      });
    } else {
      clearErrors(unitsSoldFieldName);
    }
  };

  const handleUnitsSoldBlur = () => {
    if (unitsSold && unitsSold > 0) {
      clearConsiderationForm();
    }
  };

  const isTotalRealisation =
    !!unitsSold && holdingClassTotal.currentNumberOfUnits === unitsSold;

  const hasMultipleInvestments = investmentHistory.length > 1;

  const shouldShowPartialRealisationAllocationLogic =
    selectedHoldingClassId &&
    unitsSold &&
    hasMultipleInvestments &&
    investmentsWithUnits.length > 0;

  const canDelete = totalClassesIncludedInRealisation > 1;

  return (
    <>
      <Row spacing="sm" breakpoint="md" centered justify="space-between">
        <Typography variant="h6">
          <ColorIndicatorWithText listIndex={index} text="Holding class" />
        </Typography>
        <Controller
          name={`holdingClassRealisation.${index}.holdingClassId`}
          control={control}
          render={({
            field: { ref, onChange: fieldOnChange, ...rest },
            fieldState: { error },
          }) => (
            <FormField size="md">
              <Row spacing="sm">
                <BasicSelect
                  {...rest}
                  fullWidth
                  options={holdingClassOptions}
                  disabled={holdingClassOptions.length === 0}
                  placeholder="Select existing holding class"
                  error={!!error}
                  helperText={error?.message}
                  key={`holding-class-select-${index}-${holdingClassOptions.length}`}
                  onChange={(val) => {
                    fieldOnChange(val);
                    clearErrors(unitsSoldFieldName);
                    setValue(unitsSoldFieldName, undefined, {
                      shouldValidate: true,
                    });
                    setValue(
                      selectedPartialRealisationAllocationLogicFieldName,
                      PartialRealisationAllocationLogic.ProRata,
                      { shouldValidate: true },
                    );
                  }}
                />
                {canDelete ? (
                  <IconButton
                    onClick={onRemoveHoldingClass}
                    edge="end"
                    aria-label="Remove holding class"
                  >
                    <Delete />
                  </IconButton>
                ) : null}
              </Row>
            </FormField>
          )}
        />
      </Row>
      <Divider />
      <Row spacing="xl" fullWidth breakpoint="md">
        <Column spacing="lg" fullWidth>
          <FormField size="lg" labelColorLight label="Total units held">
            <Typography variant="body2" color="grey.900">
              {numberToDisplayString(
                holdingClassTotal?.currentNumberOfUnits ?? 0,
              )}
            </Typography>
          </FormField>
          <FormField
            size="md"
            label="Units sold"
            tooltip="Add an estimated number of units if the actual number of units is unknown at the point of processing. This transaction can be edited after submission to include the actual number of units."
          >
            <Controller
              name={`holdingClassRealisation.${index}.unitsSold`}
              control={control}
              render={({
                field: { value, onChange, onBlur: _onBlur, ...restField },
                fieldState: { error },
              }) => (
                <Row centered spacing="sm">
                  <NumberInput
                    {...restField}
                    value={value ?? null}
                    onChange={(val) =>
                      handleUnitsSoldValueChange(val, onChange)
                    }
                    decimals={5}
                    fullWidth
                    placeholder="Enter amount"
                    error={!!error}
                    helperText={error?.message}
                    onBlur={handleUnitsSoldBlur}
                  />
                  <Typography variant="h6" color="grey.900">
                    <TypographyButton
                      text="All"
                      textDecoration="none"
                      onClick={() => {
                        clearErrors(unitsSoldFieldName);
                        onChange(holdingClassTotal?.currentNumberOfUnits ?? 0);
                      }}
                    />
                  </Typography>
                </Row>
              )}
            />
          </FormField>
        </Column>
        {!!shouldShowPartialRealisationAllocationLogic && (
          <>
            <Divider orientation="vertical" breakpoint="md" hideAtBreakpoint />
            <Column spacing="lg" fullWidth>
              {!isTotalRealisation && (
                <FormField
                  size="md"
                  label="Partial realisation allocation logic"
                >
                  <Controller
                    name={`holdingClassRealisation.${index}.partialRealisationAllocationLogic`}
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <BasicSelect
                        {...restField}
                        fullWidth
                        options={PARTIAL_REALISATION_ALLOCATION_OPTIONS}
                        value={value}
                        onChange={onChange}
                      />
                    )}
                  />
                </FormField>
              )}
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                <UnitToInvestmentAllocationTable
                  control={control}
                  index={index}
                  unitsAllocationPerInvestment={unitsAllocationPerInvestment}
                  onManualAllocationChange={onManualAllocationChange}
                  clearConsiderationForm={clearConsiderationForm}
                />
              )}
            </Column>
          </>
        )}
      </Row>
    </>
  );
};

export default HoldingClassRealisation;
